import { useEffect, useRef, useState } from "react";
import { Html5Qrcode } from "html5-qrcode";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from "@/components/ui/dialog";
import { Camera, Keyboard, X, AlertCircle } from "lucide-react";

// Camera + manual barcode scanner. Works on iOS Safari / Android Chrome over HTTPS.
export function BarcodeScanner({ open, onOpenChange, onResult, title = "Barcode scannen" }) {
  const [error, setError] = useState("");
  const [manual, setManual] = useState("");
  const [active, setActive] = useState(false);
  const scannerRef = useRef(null);
  const lastRef = useRef({ value: "", at: 0 });

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    const id = "wawi-scanner-region";
    const start = async () => {
      try {
        const scanner = new Html5Qrcode(id, { verbose: false });
        scannerRef.current = scanner;
        await scanner.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 260, height: 160 } },
          (decoded) => {
            const now = Date.now();
            if (decoded === lastRef.current.value && now - lastRef.current.at < 1500) return;
            lastRef.current = { value: decoded, at: now };
            if (navigator.vibrate) navigator.vibrate(60);
            onResult(decoded);
          },
          () => {}
        );
        if (!cancelled) setActive(true);
      } catch (e) {
        setError("Kamera konnte nicht gestartet werden. Bitte Berechtigung prüfen oder manuelle Eingabe nutzen.");
      }
    };
    start();
    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) { s.stop().then(() => s.clear()).catch(() => {}); scannerRef.current = null; }
      setActive(false);
    };
  }, [open, onResult]);

  const submitManual = (e) => {
    e.preventDefault();
    if (manual.trim()) { onResult(manual.trim()); setManual(""); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md" data-testid="scanner-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><Camera className="h-4 w-4" />{title}</DialogTitle>
          <DialogDescription>Richten Sie den Barcode/QR-Code im Rahmen aus. Alternativ manuell eingeben.</DialogDescription>
        </DialogHeader>
        {error ? (
          <div className="flex items-start gap-2 rounded-md border border-amber-500/30 bg-amber-500/5 p-3 text-sm text-amber-700 dark:text-amber-300">
            <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" />{error}
          </div>
        ) : (
          <div id="wawi-scanner-region" className="overflow-hidden rounded-lg border border-border bg-black/80" style={{ minHeight: 220 }} />
        )}
        <form onSubmit={submitManual} className="flex items-center gap-2">
          <Keyboard className="h-4 w-4 shrink-0 text-muted-foreground" />
          <Input value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Manuell eingeben (EAN/IMEI/SN)" data-testid="scanner-manual-input" autoFocus />
          <Button type="submit" size="sm" data-testid="scanner-manual-submit">OK</Button>
        </form>
        <Button variant="outline" onClick={() => onOpenChange(false)}><X className="mr-1.5 h-4 w-4" />Scanner schließen</Button>
      </DialogContent>
    </Dialog>
  );
}
